<script setup lang="ts">
/*
 * Mirrors the prototype's .icon-btn — square (or pill) icon-only button with
 * 1px app-border and slate icon color. Used for top-bar utilities (search,
 * notifications, settings) and inline table actions.
 */
import { computed } from 'vue';

type Size = 'sm' | 'md';

const props = withDefaults(
  defineProps<{
    size?: Size;
    label: string;
    disabled?: boolean;
    active?: boolean;
    rounded?: 'lg' | 'pill';
  }>(),
  { size: 'md', rounded: 'lg' },
);

defineEmits<{ click: [ev: MouseEvent] }>();

const dims = computed(() => (props.size === 'sm' ? 'p-1.5' : 'p-2'));
const radius = computed(() => (props.rounded === 'pill' ? 'rounded-pill' : 'rounded-lg'));
</script>

<template>
  <button
    type="button"
    :aria-label="label"
    :title="label"
    :class="[
      'inline-flex items-center justify-center border bg-app-surface text-slate-600 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
      dims,
      radius,
      active
        ? 'bg-primary-50 border-primary-100 text-primary-700'
        : 'border-app-border hover:bg-slate-50',
    ]"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <slot />
  </button>
</template>
